import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Button,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    Icon,
    Stack,
    Box,
    Spinner,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    flexRender,
} from '@tanstack/react-table';
import { FaRegEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdChevronRight, MdChevronLeft } from 'react-icons/md';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import ConfirmDialog from "components/ConfirmDialog";
import CButton from "components/CButton";
import Card from "components/card/Card";
import { useNotification } from "contexts/NotificationContext";
import { BsSend } from "react-icons/bs";
import Pagination from "components/Pagination";

export default function NotificationsOverview({ canCreate, canUpdate, canDelete }) {
    const { getAllNotifications, deleteNotification, sendNotification } = useNotification();

    const textColor = useColorModeValue('navy.700', 'white');

    const [globalFilter, setGlobalFilter] = useState('');
    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [notificationToDelete, setNotificationToDelete] = useState(null);
    const [isSendDialogOpen, setIsSendDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [notificationToSend, setNotificationToSend] = useState(null);
    const [filter, setFilter] = useState({
        search: '',
        limit: 10,
        page: 1
    });

    const navigate = useNavigate();

    const handleDelete = async () => {
        if (notificationToDelete) {
            try {
                await deleteNotification(notificationToDelete);
                setNotificationToDelete(null);
                fetchNotifications();
            } catch (error) {
                console.error("Failed to delete notification:", error);
            }
            setIsDeleteDialogOpen(false);
        }
    };

    const openDeleteDialog = (notificationId) => {
        setNotificationToDelete(notificationId);
        setIsDeleteDialogOpen(true);
    };

    const openSendDialog = (notificationId) => {
        setNotificationToSend(notificationId);
        setIsSendDialogOpen(true);
    };

    const handleSendNotification = async () => {
        if (notificationToSend) {
            try {
                await sendNotification(notificationToSend);
                setNotificationToSend(null);
            } catch (error) {
                console.error("Failed to send notification:", error);
            }
            setIsSendDialogOpen(false);
        }
    };

    const columnHelper = createColumnHelper();
    const columns = useMemo(() => {
        const baseColumns =
            [
                columnHelper.accessor('title', {
                    header: () => <Text color="gray.400">TITLE</Text>,
                    cell: (info) => (
                        <Text color={textColor} fontSize="md" fontWeight="500">
                            {info.getValue()}
                        </Text>
                    ),
                }),
                columnHelper.accessor('description', {
                    header: () => <Text color="gray.400">DESCRIPTION</Text>,
                    cell: (info) => (
                        <Text color={textColor} fontSize="md" fontWeight="500" className="ellipsis-wrap" width='300px'>
                            {info.getValue()}
                        </Text>
                    ),
                }),
                columnHelper.accessor('scheduled_at', {
                    header: () => <Text color="gray.400">SCHEDULED AT</Text>,
                    cell: (info) => (
                        <Text color={textColor} fontSize="md" fontWeight="500">
                            {info.getValue()}
                        </Text>
                    ),
                }),
            ]

        if (canCreate || canUpdate || canDelete) {
            baseColumns.push(
                columnHelper.accessor('actions', {
                    header: () => <Text color="gray.400">ACTIONS</Text>,
                    cell: (info) => (
                        <Flex>
                            {canUpdate && <Icon
                                as={FaRegEdit}
                                h={6}
                                w={6}
                                mt='10px'
                                me='20px'
                                onClick={() => navigate(`/admin/notifications/edit/${info.row.original.id}`)}
                                cursor='pointer'
                                title="Edit"
                                color='#fa8f03'
                            />}
                            {canDelete && <Icon
                                as={RiDeleteBin6Line}
                                h={6}
                                w={6}
                                mt='10px'
                                me='20px'
                                onClick={() => openDeleteDialog(info.row.original.id)}
                                cursor='pointer'
                                title="Delete"
                                color='#fa8f03'
                            />}
                            {canCreate && <Icon
                                as={BsSend}
                                h={6}
                                w={6}
                                mt='10px'
                                me='20px'
                                cursor='pointer'
                                title="Send Notifications"
                                color='#fa8f03'
                                onClick={() => openSendDialog(info.row.original.id)}
                            />}
                        </Flex>
                    ),
                }));
        }
        return baseColumns;
    }, [canCreate, canUpdate, canDelete]);

    const [{ pageIndex, pageSize }, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });

    const pagination = useMemo(
        () => ({ pageIndex, pageSize }),
        [pageIndex, pageSize],
    );

    const table = useReactTable({
        data,
        columns,
        state: { globalFilter, pagination },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        fetchNotifications();
    }, [filter.search, filter.limit, filter.page]);

    const fetchNotifications = async () => {
        setIsLoading(true);
        try {
            const data = await getAllNotifications({
                search: filter.search,
                limit: filter.limit,
                page: filter.page,
            });
            if (data && data.notifications) {
                setData(data.notifications);
                setTotalPages(data.totalPages || 0)
            } else {
                console.warn("No notifications found or data is undefined");
                setData([]);
            }
        } catch (error) {
            console.error("Failed to fetch notifications:", error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePageChange = (newPageIndex) => {
        setPagination((prev) => ({
            ...prev,
            pageIndex: newPageIndex,
        }));
        setFilter((prev) => ({
            ...prev,
            page: newPageIndex,
        }));
    };

    return (
        <Flex direction='column' pt={{ sm: "100px", lg: "55px" }}>
            <ConfirmDialog
                position="top"
                isOpen={isSendDialogOpen}
                onClose={() => setIsSendDialogOpen(false)}
                onConfirm={() => handleSendNotification(notificationToSend)}
                header="Notification Confirmation"
                message="Are you sure you want to send this notification?"
                isDelete={false}
            />
            <ConfirmDialog
                position='top'
                isOpen={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                onConfirm={handleDelete}
                header="Delete Confirmation"
                message="Are you sure you want to delete this notification?"
            />

            <Card px='0px' minHeight="80vh">
                <Flex direction="column" w="100%" overflowX="auto">
                    <Flex align="center" justify="space-between" w="100%" px="22px" mb="36px">
                        <DebouncedInput
                            value={globalFilter ?? ''}
                            onChange={(value) => setFilter((prev) => ({ ...prev, search: value, page: 1 }))}
                            placeholder="Search..."
                        />
                        {canCreate && <Flex gap='16px'>
                            <CButton onClick={() => navigate('/admin/notifications/create')}>
                                Add Notification
                            </CButton>
                        </Flex>}
                    </Flex>
                    {isLoading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            borderRadius="10px"
                            overflow="hidden"
                            minHeight="500px"
                        >
                            <Spinner color="#fa8f03" />
                        </Box>
                    ) : (
                        <>
                            <Box border="2px solid #fa8f03" borderRadius="10px" overflow="auto" maxHeight="520px">
                                <Table variant="simple">
                                    <Thead>
                                        {table.getHeaderGroups().map((headerGroup) => (
                                            <Tr key={headerGroup.id} bg='#fa8f03'>
                                                {headerGroup.headers.map((header) => (
                                                    <Th key={header.id} borderColor='#fa8f03'>
                                                        {header.isPlaceholder ? null : (
                                                            <Flex
                                                                onClick={header.column.getToggleSortingHandler()}
                                                                cursor={header.column.getCanSort() ? 'pointer' : 'default'}
                                                                justify="space-between"
                                                                align="center"
                                                                color="white"
                                                            >
                                                                {flexRender(header.column.columnDef.header, header.getContext())}
                                                            </Flex>
                                                        )}
                                                    </Th>
                                                ))}
                                            </Tr>
                                        ))}
                                    </Thead>
                                    <Tbody>
                                        {data.length > 0 ? (
                                            table.getRowModel().rows.map((row) => (
                                                <Tr key={row.id}>
                                                    {row.getVisibleCells().map((cell) => (
                                                        <Td key={cell.id} fontSize="sm" borderColor='#fa8f03'>
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </Td>
                                                    ))}
                                                </Tr>
                                            ))
                                        ) : (
                                            <Tr>
                                                <Td colSpan={columns.length} textAlign="center">
                                                    No notifications found.
                                                </Td>
                                            </Tr>
                                        )}
                                    </Tbody>
                                </Table>
                            </Box>
                            <Pagination totalPages={totalPages} currentPage={filter.page} onPageChange={handlePageChange} />
                        </>
                    )}
                </Flex>
            </Card>
        </Flex>
    );
}

function DebouncedInput({ value: initialValue, onChange, debounce = 500, ...props }) {
    const [value, setValue] = useState(initialValue);

    useEffect(() => {
        setValue(initialValue);
    }, [initialValue]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value);
        }, debounce);
        return () => clearTimeout(timeout);
    }, [value, debounce]);

    return (
        <SearchBar
            value={value}
            onChange={(e) => setValue(e.target.value)}
            h="44px"
            w={{ lg: '390px' }}
            borderRadius="16px"
            {...props}
        />
    );
}
